import { z } from 'zod';

/**
 * The one authoring model behind every M8 renderer.
 *
 * P0 deliberately contains only the non-geometric presets. Labeled graphics,
 * guided images and maps are M16 presets and must consume M16's geometry engine
 * rather than growing a second hit-testing implementation here.
 */
export const interactionTypeSchema = z.enum(['tabs', 'accordion', 'timeline']);
export type InteractionType = z.infer<typeof interactionTypeSchema>;

export const interactionThemeSchema = z.object({
  colorSource: z.enum(['inherit-from-deck', 'custom']).default('inherit-from-deck'),
  palette: z
    .object({
      accent: z.string().default('#2F5D8C'),
      surface: z.string().default('#FFFFFF'),
      text: z.string().default('#1A1A1A'),
    })
    .default({}),
  fontScale: z.number().min(0.75).max(2).default(1),
});

export const interactionLayoutSchema = z.object({
  orientation: z.enum(['horizontal', 'vertical']).default('horizontal'),
  columnCount: z.number().int().min(1).max(4).default(1),
  mediaPosition: z.enum(['start', 'end', 'top', 'bottom']).default('top'),
});

export const interactionNavigationSchema = z.object({
  showPrevNext: z.boolean().default(true),
  requireSequential: z.boolean().default(false),
  trackViewedItems: z.boolean().default(true),
});

export const interactionCompletionSchema = z.object({
  rule: z.enum(['AllItemsViewed', 'NItemsViewed', 'None']).default('AllItemsViewed'),
  /** Item count when rule=NItemsViewed. Clamped against the real item count at runtime. */
  threshold: z.number().int().min(1).default(1),
});

export const interactionMediaSchema = z.object({
  kind: z.enum(['image', 'video', 'audio', 'none']).default('none'),
  assetId: z.string().optional(),
  altText: z.string().default(''),
  caption: z.string().default(''),
});

export const interactionItemSchema: z.ZodType<InteractionItem, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.object({
    id: z.string().min(1),
    label: z.string().default(''),
    shortLabel: z.string().default(''),
    /**
     * Portable source text. Paragraph breaks are preserved and rendered as
     * text, never injected as HTML. This keeps imported CSV/clipboard content
     * from becoming a script execution path in ten-year course packages.
     */
    body: z.string().default(''),
    /** Optional TeX source. Typesetting is a later renderer capability. */
    latex: z.string().default(''),
    media: interactionMediaSchema.default({}),
    date: z
      .object({
        display: z.string().default(''),
        sortKey: z.string().default(''),
      })
      .optional(),
    children: z.array(interactionItemSchema).default([]),
  }),
);

export interface InteractionItem {
  readonly id: string;
  readonly label: string;
  readonly shortLabel: string;
  readonly body: string;
  readonly latex: string;
  readonly media: z.infer<typeof interactionMediaSchema>;
  readonly date?: { readonly display: string; readonly sortKey: string };
  readonly children: readonly InteractionItem[];
}

export const interactionSchema = z.object({
  id: z.string().min(1),
  type: interactionTypeSchema,
  title: z.string().default(''),
  description: z.string().default(''),
  theme: interactionThemeSchema.default({}),
  layout: interactionLayoutSchema.default({}),
  navigation: interactionNavigationSchema.default({}),
  completion: interactionCompletionSchema.default({}),
  items: z.array(interactionItemSchema).default([]),
});

export type Interaction = z.infer<typeof interactionSchema>;

export type InteractionIssueCode =
  | 'empty-title'
  | 'empty-items'
  | 'duplicate-item-id'
  | 'empty-label'
  | 'empty-body'
  | 'missing-date'
  | 'missing-media'
  | 'missing-alt-text'
  | 'threshold-out-of-range';

export interface InteractionIssue {
  readonly code: InteractionIssueCode;
  readonly itemId?: string;
}

/** Authoring validation is separate from schema parsing so drafts remain savable. */
export function validateInteraction(interaction: Interaction): readonly InteractionIssue[] {
  const issues: InteractionIssue[] = [];
  if (interaction.title.trim().length === 0) issues.push({ code: 'empty-title' });
  if (interaction.items.length === 0) issues.push({ code: 'empty-items' });

  const ids = new Set<string>();
  for (const item of interaction.items) {
    if (ids.has(item.id)) issues.push({ code: 'duplicate-item-id', itemId: item.id });
    ids.add(item.id);
    if (item.label.trim().length === 0) issues.push({ code: 'empty-label', itemId: item.id });
    if (item.body.trim().length === 0 && item.latex.trim().length === 0) {
      issues.push({ code: 'empty-body', itemId: item.id });
    }
    if (interaction.type === 'timeline' && !item.date?.display.trim()) {
      issues.push({ code: 'missing-date', itemId: item.id });
    }
    if (item.media.kind !== 'none') {
      if (!item.media.assetId) issues.push({ code: 'missing-media', itemId: item.id });
      if ((item.media.kind === 'image' || item.media.kind === 'video') && !item.media.altText.trim()) {
        issues.push({ code: 'missing-alt-text', itemId: item.id });
      }
    }
  }

  if (
    interaction.completion.rule === 'NItemsViewed' &&
    interaction.items.length > 0 &&
    interaction.completion.threshold > interaction.items.length
  ) {
    issues.push({ code: 'threshold-out-of-range' });
  }
  return issues;
}

export function requiredViewedItems(interaction: Interaction): number {
  switch (interaction.completion.rule) {
    case 'None':
      return 0;
    case 'AllItemsViewed':
      return interaction.items.length;
    case 'NItemsViewed':
      return Math.min(interaction.items.length, interaction.completion.threshold);
  }
}

export function createInteraction(type: InteractionType, id: string): Interaction {
  return interactionSchema.parse({
    id,
    type,
    title: '',
    layout: { orientation: type === 'timeline' ? 'vertical' : 'horizontal' },
  });
}

export function createInteractionItem(id: string): InteractionItem {
  return interactionItemSchema.parse({ id });
}
